import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const LanguageContext = createContext();

// Get browser language (returns "en" or "es" if supported, otherwise "en")
const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split("-")[0].toLowerCase();
  // Only return if it's a supported language
  return ["en", "es"].includes(langCode) ? langCode : "en";
};

// Get stored language from localStorage
const getStoredLanguage = () => {
  try {
    return localStorage.getItem("finflow_language") || null;
  } catch {
    return null;
  }
};

// Store language in localStorage
const storeLanguage = (lang) => {
  try {
    localStorage.setItem("finflow_language", lang);
  } catch {
    // Ignore storage errors
  }
};

// Cache version - increment this when translations are updated
const CACHE_VERSION = 5;

// Get cached translations from localStorage
const getCachedTranslations = (lang) => {
  try {
    const cached = localStorage.getItem(`finflow_translations_${lang}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if cache is less than 1 hour old AND cache version matches
      if (parsed.timestamp && Date.now() - parsed.timestamp < 3600000 && parsed.version === CACHE_VERSION) {
        return parsed.data;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
};

// Cache translations in localStorage
const cacheTranslations = (lang, data) => {
  try {
    localStorage.setItem(`finflow_translations_${lang}`, JSON.stringify({
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    }));
  } catch {
    // Ignore cache errors
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Priority: stored > browser > default (en)
    return getStoredLanguage() || getBrowserLanguage();
  });
  const [translations, setTranslations] = useState({});
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch available languages
  const fetchLanguages = useCallback(async () => {
    try {
      const res = await api.get("/languages");
      setAvailableLanguages(res.data);
      return res.data;
    } catch (error) {
      console.error("Failed to fetch languages:", error);
      // Default fallback
      setAvailableLanguages([
        { code: "en", name: "English", native_name: "English", is_default: true },
        { code: "es", name: "Spanish", native_name: "Español", is_default: false }
      ]);
      return [];
    }
  }, []);

  // Fetch translations for a language
  const fetchTranslations = useCallback(async (lang) => {
    // Try cache first
    const cached = getCachedTranslations(lang);
    if (cached) {
      setTranslations(cached);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await api.get(`/translations/${lang}`);
      setTranslations(res.data);
      cacheTranslations(lang, res.data);
    } catch (error) {
      console.error(`Failed to fetch translations for ${lang}:`, error);
      // Fallback to empty, will show keys
      setTranslations({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Change language
  const setLanguage = useCallback(async (newLang) => {
    if (newLang === language) return;
    
    setLanguageState(newLang);
    storeLanguage(newLang);
    await fetchTranslations(newLang);
  }, [language, fetchTranslations]);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      await fetchLanguages();
      await fetchTranslations(language);
    };
    init();
  }, [fetchLanguages, fetchTranslations, language]);

  // Translation function
  // Usage: t("key", "namespace") or t("key") for common namespace
  const t = useCallback((key, namespace = "common", params = {}) => {
    // Get translation from namespace
    let text = translations[namespace]?.[key];
    
    // Fallback to common namespace if not found
    if (!text && namespace !== "common") {
      text = translations["common"]?.[key];
    }
    
    // Fallback to key itself if not found
    if (!text) {
      text = key;
    }
    
    // Interpolate params (e.g., t("hello_name", "common", {name: "John"}) => "Hello, John!")
    if (params && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        text = text.replace(new RegExp(`{${paramKey}}`, "g"), paramValue);
      });
    }
    
    return text;
  }, [translations]);

  // Get detected browser language
  const browserLanguage = getBrowserLanguage();

  // Check if a language is available
  const isLanguageAvailable = useCallback((langCode) => {
    return availableLanguages.some(l => l.code === langCode && l.is_enabled !== false);
  }, [availableLanguages]);

  // Clear translation cache (useful when admin updates translations)
  const clearCache = useCallback(() => {
    try {
      availableLanguages.forEach(lang => {
        localStorage.removeItem(`finflow_translations_${lang.code}`);
      });
    } catch {
      // Ignore errors
    }
  }, [availableLanguages]);

  // Refresh translations from server (bypassing cache)
  const refreshTranslations = useCallback(async () => {
    clearCache();
    await fetchTranslations(language);
  }, [clearCache, fetchTranslations, language]);

  const value = {
    language,
    setLanguage,
    t,
    translations,
    availableLanguages,
    isLoading,
    browserLanguage,
    isLanguageAvailable,
    clearCache,
    refreshTranslations,
    fetchLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export default LanguageContext;
